import { OverlayContainer } from '@angular/cdk/overlay';
import { AfterViewChecked, AfterViewInit, Component, HostBinding, Inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { MsalBroadcastService, MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import $ from "jquery";
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import packageInfo from '../../package.json';
import { AlertDataForNativeMobile } from './classes/alert-native-mobile';
import { IOScoords } from './classes/geo-location-object';
import { Profile } from './classes/profile';
import { ProfileUpdate } from './classes/profile-update';
import { Van } from './classes/van';
import { BRAND_ICON_URL, IMPERSONATION_KEY, LIGHT_MAP_NAME, PROFILE_KEY } from './common/constants';
import { Roles } from './common/roles';
import { DriverWelcomeModalComponent } from './components/driver-welcome-modal/driver-welcome-modal.component';
import { ProfileComponent } from './components/profile/profile.component';
import { AlertService } from './services/alert.service';
import { AssetService } from './services/asset.service';
import { CoordinatesService } from './services/coordinates.service';
import { DispatchService } from './services/dispatch.service';
import { EventsDeltaService } from './services/events-delta.service';
import { EventsService } from './services/events.service';
import { GeoLocationService } from './services/geo-location.service';
import { IdleTimerService } from './services/idle-timer.service';
import { IosService } from './services/ios.service';
import { LoggerService } from './services/logger.service';
import { MapLayerService } from './services/map-layer.service';
import { ProfileService } from './services/profile.service';
import { SettingsService } from './services/settings.service';
import { TaskMarkersService } from './services/task-markers.service';
import { ToastService } from './services/toast.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: [ './app.component.scss' ],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit {
  @HostBinding('class') className = '';
  public title = `CVV ${packageInfo.version}`;
  public profile: Profile = new Profile();
  public panelTitle = 'profile';
  public toggleControl = new FormControl(false);
  public BRAND_ICON_URL = BRAND_ICON_URL;
  @ViewChild('IOSTEST', {static:false}) public iostest: any;


  isIframe = false;
  private profileLoaded = false; // Prevent loading profile multiple times
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private profileService: ProfileService,
    private alertService: AlertService,
    private assetService: AssetService,
    private taskMarkersService: TaskMarkersService,
    private overlay: OverlayContainer,
    private mapLayerService: MapLayerService,
    private logger: LoggerService,
    private toastService: ToastService,
    private geoLocationService: GeoLocationService,
    private idleTimerService: IdleTimerService,
    private eventsService: EventsService,
    private eventsDeltaService: EventsDeltaService,
    private dispatchService: DispatchService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    public dialog: MatDialog,
    private settingsService: SettingsService,
    private zone: NgZone,
    private iOSService: IosService,
    private coordinatesService: CoordinatesService
  ) {
    this.registerMaterialIcons();
  }

  ngAfterViewInit(): void {
    // this.settingsService._themeConfig.subscribe((data: Boolean) => {
    //   this.toggleDarkMode(data);
    // });

    // let userPrefTheme = localStorage.getItem('prefAppTheme');
    // if ( userPrefTheme == null || typeof userPrefTheme == "undefined") {
    //   localStorage.setItem('prefAppTheme', "false");
    //   this.toggleDarkMode(false);
    // } else {
    //   const userPrefThemeFlag = userPrefTheme === "true" ? true : false;
    //   this.toggleDarkMode(userPrefThemeFlag);
    // }
  }

  ngOnInit(): void {
    const component = this; // <-- used to access angular zone from jquery zone
    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal
    // localStorage.setItem('cvvAlertList', '[]');
    localStorage.filter_dispatch_on = 0;
    this.logger.getLocalStorageSize();

    this.msalBroadcastService.inProgress$
      .pipe(takeUntil(this._destroying$))
      .subscribe((status) => {
        this.logger.debug(
          `[AuthFlow][AppComponent] inProgress status=${InteractionStatus[status]} active=${this.authService.instance.getActiveAccount()?.username || 'none'} accounts=${this.authService.instance.getAllAccounts().length}`,
          'auth'
        );
      });

    this.msalBroadcastService.msalSubject$
      .pipe(takeUntil(this._destroying$))
      .subscribe((msg: EventMessage) => {
        this.logger.debug(
          `[AuthFlow][AppComponent] msal event=${EventType[msg.eventType]} interaction=${msg.interactionType || 'none'} error=${msg.error ? 'yes' : 'no'}`,
          msg,
          'auth'
        );
      });

    // Wait for MSAL to finish handling any redirect before loading profile
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.logger.debug('[AuthFlow][AppComponent] InteractionStatus.None reached; checking active account', 'auth');
        this.checkAndSetActiveAccount();
        const cachedProfile = this.profileService.profileObs$.getValue();
        
        // Only load profile once and if user is authenticated
        if (!this.profileLoaded && cachedProfile) {
          this.logger.debug('[AuthFlow][AppComponent] using cached profile from ProfileService', 'auth');
          this.profileLoaded = true;
          this.initializeAfterProfileLoad(cachedProfile);
        } else if (!this.profileLoaded && this.authService.instance.getAllAccounts().length > 0) {
          this.logger.debug('[AuthFlow][AppComponent] loading user profile after auth completion', 'auth');
          this.profileLoaded = true;
          this.loadUserProfile();
        } else {
          this.logger.debug(
            `[AuthFlow][AppComponent] skipping loadUserProfile profileLoaded=${this.profileLoaded} accounts=${this.authService.instance.getAllAccounts().length}`,
            'auth'
          );
        }
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe(() => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          this.logger.warn('[AuthFlow][AppComponent] account removed and no accounts remain; forcing navigation to /');
          window.location.pathname = '/';
        }
    });

    // forced to use jquery here.
    $(document).on('touchstart click', '.buttonAssignTaskFromMarker', function () {
      const idName = $(this).attr('id');
      const idNumber = idName.split('_');
      const tskId = idNumber[1];
      component.zone.run(() => {
        component.callAngular(tskId, "assignFromMarkerPopup");
      });
    });


    let userPrefTheme = localStorage.getItem('prefAppTheme');
    if ( userPrefTheme == null || typeof userPrefTheme == "undefined") {
      localStorage.setItem('prefAppTheme', "false");
      this.toggleDarkMode(false);
    } else {
      const userPrefThemeFlag = userPrefTheme === "true" ? true : false;
      this.toggleDarkMode(userPrefThemeFlag);
    }
    this.settingsService.onThemeChanged().pipe(takeUntil(this._destroying$)).subscribe(flag => this.toggleDarkMode(flag));

    this.logger.info('App Initialized');
  }

  /**
   * Loads user profile after MSAL authentication is complete.
   * Extracted to avoid calling during redirect handling.
   */
  private loadUserProfile(): void {
    this.logger.debug('[AuthFlow][AppComponent] loadUserProfile invoked', 'auth');
    this.profileService.postProfile().subscribe((resp) => {
      this.initializeAfterProfileLoad(resp.body);
    }, (error) => {
      this.logger.error('[AuthFlow][AppComponent] loadUserProfile failed', error);
    });
  }

  private initializeAfterProfileLoad(profile: Profile): void {
    this.profile = profile;
    this.logger.debug(
      `[AuthFlow][AppComponent] profile loaded; adId=${this.profile?.adId || 'unknown'} roles=${JSON.stringify(this.profile?.userRoles || [])}`,
      'auth'
    );
    if (this.profile.userRoles.includes(Roles.DRIVER)) {
      this.vanProfileCheck(this.profile);
    }
    this.geoLocationService.startGeoLocation();
    this.coordinatesService.init();
    this.assetService.startAssetInterval();
    this.eventsService.startEventInterval();
    this.eventsDeltaService.startEventDeltaInterval();
    this.alertService.startAlertInterval();
    if (this.profile.userRoles.includes(Roles.COORDINATOR)) {
      this.dispatchService.startDispatchInterval();
      this.setCoordinatorRoleForNativeIOS(true);
    }
    this.idleTimerService.initIdleTimeout();
    this.subscribeToAlertsForNativeMobile();
    this.impersonationCheck();
  }

  impersonationCheck(): void{
    if(IMPERSONATION_KEY in localStorage){
      localStorage.removeItem(IMPERSONATION_KEY);
      this.toastService.show(`Impersonation Active - Refresh to Quit.`, {
        classname: "bg-info text-dark",
        delay: 6000,
      });
    }
  }

  // getAssignedVan(vanList: Array<Van>, profile: Profile): string | null{
  //   let van = null;
  //   vanList.forEach((element) => {
  //     if (element.driverId.toUpperCase() == profile.adId.toUpperCase()) {
  //       van = element.vanId;
  //     }
  //   });
  //   return van
  // }

  getLastUsedVan(): ProfileUpdate | null{
      const localStorageString = localStorage.getItem(PROFILE_KEY);
      return localStorageString !== null ? JSON.parse(localStorageString) : null;
  }

  isSomeOneSeriouslyUsingMyVan(vanList: Array<Van>, profile: Profile): string{
    const lastVan = this.getLastUsedVan()?.vanDriverVanNumberPref || null;
    let vanInUseBy = null;
    if(lastVan !== null && lastVan !== ''){
      vanList.forEach((element) => {
        if ((element.driverId !== null && element.driverId !== '') && (lastVan === element.vanId) && (element.driverId.toUpperCase() !== profile.adId.toUpperCase())) {
          console.log("SOMEONE IS USING MY VAN!");
          console.log(vanInUseBy);
          vanInUseBy = element.driverName;
        }
      });
    }
    return vanInUseBy;
  }

  vanProfileCheck(profile:Profile){
    const vanList: Array<Van> = profile.vanList;
    let isFirstTimeDriver = this.getLastUsedVan() !== null ? false : true;
    let isMyVanTaken = this.isSomeOneSeriouslyUsingMyVan(vanList, profile) === null ? false : true;
    let lastVanUsed = this.getLastUsedVan()?.vanDriverVanNumberPref;
    // let savedTasks = JSON.parse(localStorage.getItem(SAVED_STARTED_TASKS_KEY)) || undefined;
      if(isFirstTimeDriver){
        this.openFirstTimeDriverDialog();
      }else if(isMyVanTaken){
        this.openDriverWelcomeDialog('duplicateVan', this.isSomeOneSeriouslyUsingMyVan(vanList, profile), null, vanList);
      // }else if(savedTasks !== undefined && savedTasks?.length > 0){
      //   console.log("SAVED TASKS FOUND", savedTasks)
      //   this.openDriverWelcomeDialog('savedTasks', null, null, vanList);
      }else if(lastVanUsed !== null && lastVanUsed !== ''){
        this.openDriverWelcomeDialog('lastVanUsed', null, lastVanUsed, vanList);
      }else{
        alert("vanProfileCheck: You should not see this error.");
      }
  }


  openFirstTimeDriverDialog() {
    const dialogRef = this.dialog.open(ProfileComponent, {
      data: {type: 'firstTimeDriver'},
      disableClose: true
    });
    dialogRef.afterClosed().subscribe(result => {
      this.logger.debug(`Dialog result: ${result}`, 'ui');
    });
  }

  openDriverWelcomeDialog(type, vanInUseBy, lastVanUsed, vanList){
    const dialogRef = this.dialog.open(DriverWelcomeModalComponent, {
      data: {
        type: type,
        vanInUseBy: vanInUseBy,
        lastVanUsed: lastVanUsed,
        vanList: vanList
      },
      disableClose: true,
      width: '350px'
    });
    dialogRef.afterClosed().subscribe(result => {
      this.logger.debug(`Dialog result: ${result}`, 'ui');
    });
  }

  callAngular(tskId, assignFrom){
    this.logger.debug("ANGULAR CALLED", 'ui');
    this.taskMarkersService.assignTask(tskId, assignFrom);
  }


  checkAndSetActiveAccount(): void {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
      this.logger.debug(`[AuthFlow][AppComponent] active account was empty; set to ${accounts[0]?.username || 'unknown'}`, 'auth');
    } else {
      this.logger.debug(
        `[AuthFlow][AppComponent] active account already set=${activeAccount?.username || 'none'} allAccounts=${this.authService.instance.getAllAccounts().length}`,
        'auth'
      );
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  toggleDarkMode(darkMode: boolean): void {

    const darkClassName = 'darkMode';
    this.className = darkMode ? darkClassName : '';
    this.settingsService.updateDarkModeEnabled(darkMode);
    if (darkMode) {
      this.overlay.getContainerElement().classList.add(darkClassName);
      this.mapLayerService.updateActiveLayer("Worldport Night");
      this.toastService.show('Darkmode Enabled', { classname: 'bg-dark text-light', delay: 1500 });
    } else {
      this.overlay.getContainerElement().classList.remove(darkClassName);
      // this.mapLayerService.updateActiveLayer(LIGHT_MAP_NAME);
      this.mapLayerService.updateActiveLayer("Worldport Day");
    }
  }

  openMyTasks(event: any){
    console.log(event);
  }




  registerMaterialIcons(): void{
      this.matIconRegistry.addSvgIcon('terminal-run',
        this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/fa-terminal.svg'));
      this.matIconRegistry.addSvgIcon('calendar-days',
        this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/calendar-alt-regular.svg'));
      this.matIconRegistry.addSvgIcon('icon-bus',
        this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/fa-bus.svg'));
      this.matIconRegistry.addSvgIcon('icon-tasks',
        this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/fa-tasks.svg'));
      this.matIconRegistry.addSvgIcon('icon-unassigned',
        this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/fa-unassigned.svg'));
      this.matIconRegistry.addSvgIcon('icon-new-task',
        this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/fa-new-task.svg'));
      this.matIconRegistry.addSvgIcon('icon-airplane-menu',
        this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/fa-airplane-black.svg'));
      this.matIconRegistry.addSvgIcon('icon-dispatch',
        this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/fa-dispatch.svg'));
      this.matIconRegistry.addSvgIcon('icon-search',
        this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/fa-search.svg'));
      this.matIconRegistry.addSvgIcon('icon-user',
        this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/fa-user.svg'));
      this.matIconRegistry.addSvgIcon('icon-cog',
        this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/fa-cog.svg'));
      this.matIconRegistry.addSvgIcon('icon-paperwork',
        this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/file-alt-regular.svg'));
      this.matIconRegistry.addSvgIcon('icon-catering',
        this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/utensils-solid.svg'));
      this.matIconRegistry.addSvgIcon('icon-other',
        this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/fa-tasks.svg'));
      this.matIconRegistry.addSvgIcon('icon-eraser',
        this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/eraser-solid.svg'));
      this.matIconRegistry.addSvgIcon('icon-file',
        this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/file-alt-regular.svg'));
  }


  setCoordinatorRoleForNativeIOS(hasRole: boolean) {
    /** iOS Swift code is subscribed to this message */
    if (typeof (window as any).webkit !== 'undefined') {
      (window as any).webkit.messageHandlers.setCoordinatorRoleForNativeIOS.postMessage({
        hasCoordinatorRole: hasRole
      });
     // this.iostest.nativeElement.innerHTML = "setCoordinatorRoleForNativeIOS fired";
    } else {
    //  this.iostest.nativeElement.innerHTML = "You are using a normal browser so nothing fired for setCoordinatorRoleForNativeIOS()";
    }
  }

  subscribeToAlertsForNativeMobile(): void{
    this.alertService.alertForNativeMobile$.pipe(takeUntil(this._destroying$)).subscribe((data: AlertDataForNativeMobile) => {
      if(data){
      //  console.log("alertForNativeMobile fired");
      //  console.log(data);
        /** iOS Swift code is subscribed to this message */
        if (typeof (window as any).webkit !== 'undefined') {
          (window as any).webkit.messageHandlers.alertReceivedForNativeMobile.postMessage({
                msg: data.message,
                alertType: data.type,
            });
           // this.iostest.nativeElement.innerHTML = `alertReceivedForNativeMobile fired for native app: message=${data.message}, type=${data.type}`;
        } else {
        //  this.iostest.nativeElement.innerHTML = "You are using a normal browser so nothing fired for alertReceivedForNativeMobile()";
        }
      }
    });
  }


  locFromNative(data: IOScoords) {
  //  console.log( JSON.stringify(data)); // <=== data passed from native ios app webview to Angular
  //  this.iostest.nativeElement.innerHTML = JSON.stringify(data);
    this.iOSService.updateLocationData(data);
  }

  ngAfterViewChecked(): void {
    (window as any)['componentRef'] = {
      zone: this.zone,
      componentFn: (value: any) => this.locFromNative(value),
      component: this
    };
  }


}
